<template>
  <v-form @submit.prevent="validate() && submit()" ref="form">
    <v-card v-if="totalize" :dark="dark" class="mt-6 pa-0">
      <v-card-text>
        <v-toolbar flat dense short height=35>
          <v-toolbar-title :class="`subtitle-2 font-weight-bold ${color}--text ml-n4`">Totais</v-toolbar-title>
        </v-toolbar>
        <v-divider class="mb-3"></v-divider>
        
        <v-list-item class="ma-0 pa-0">
          <v-list-item-content class="ma-0 pa-0 d-inline-block">
            <v-list-item-subtitle v-for="({ label, text }, idx, key) in totalize" :key="key">
              <b>{{label}}:</b>  {{text}}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-card-text>
    </v-card>

    <v-card :dark="dark" class="mt-6 pa-0">
      <v-card-text>
        <FormDataTableToolbar
          :readonly="readonly"
          :exportXls="exportXls"
          :color="color"
          @exportXlsx="exportXlsx"
          @importXlsx="importXlsx"
        >
          <template #prepend-actions>
            <FormDataTableBatch
              v-if="!readonly"
              :table="table"
              :headers="headers"
              @apply="applyBatch"
            >
              <template #append>
                <v-card v-show="!isFiltered" flat color="warning lighten-3">
                  <v-card-text>
                    <v-icon left>mdi-alert</v-icon>
                    ALERTA: Nenhum filtro foi selecionado, as alterações serão aplicadas em TODOS os itens.
                  </v-card-text>
                </v-card>
              </template>
            </FormDataTableBatch>
          </template>
        </FormDataTableToolbar>
        <v-divider class="mb-3"></v-divider>

        <v-data-table
          v-if="computedFilters && computedFilters.length > 0"
          :headers="computedHeaders"
          :items="table ? table.rows : []"
          :search="search.value"
          :footer-props="{ itemsPerPageOptions: [30, 50, 100, -1] }"
          :custom-sort="customSort"
          :custom-filter="customFilter"
          dense
          item-key="id"
          loading-text="Carregando... Por favor aguarde"
          @current-items="currentItems = $event"
          @update:items-per-page="itemsPerPage = $event; scrollTop()"
          ref="dataTable"
          >
          <template #top>
            <!-- Filters | Search -->
            <FormDataTableFilters
              ref="form-data-table-filters"
              :filters="computedFilters"
              :filtersModels="filtersModels"
              :search="search"
            />

            <!-- Validations Errors -->
            <v-row v-show="invalidCells.length > 0" no-gutters class="mb-4">
              <v-col cols="12">
                <FormDataTableErrors
                  :table="table"
                  @click:goToCell="scrollToCell"
                  @change:invalidCells="invalidCells = $event"
                />
              </v-col>
            </v-row>
          </template>

          <!-- Item -->
          <template #item="{ item: row, index: line }">
            <tr
              class="form-data-table__row"
              :class="{ 'red lighten-4': !row.validated() }"
              :ref="`form-data-table-tr--${line}`"
              >  
              <td
                v-for="(cell, col) in row.cells"
                v-show="!cell.header.hide"
                :key="line+col"
                :ref="`form-data-table-td--${line}`"
                :id="`line-${cell.y}`"
                class="pa-0 ma-0 text-center"
                >
                <button
                  v-show="editing != cell"
                  :ref="`form-data-table__button--${col}-${line}`"
                  :title="cell.validated ? undefined : cell.errorMessages.map(str => str.replace(/<[^>]*>/g, '')).reduce((acc,cur) => `${acc}; ${cur}`) "
                  :class="`form-data-table__button ${(cell.header.align == 'center') ? 'mx-auto' : (cell.header.align == 'end') ? 'ml-auto' : '' }  ${(cell.header.editable) ? 'primary--text' : ''} ${cell.validated ? '' : 'form-data-table__button--invalid'}`"
                  @keydown.up="line == 0 || focusButton(col, line-1)"
                  @keydown.down="line == currentItems.length-1 || focusButton(col, line+1)"
                  @keydown.left="col == 0 || focusButton(col-1, line)"
                  @keydown.right="col == computedHeaders.length-1 || focusButton(col+1, line)"
                  @keydown.exact.tab.prevent="col == computedHeaders.length-1 || focusButton(col+1, line)"
                  @keydown.shift.tab.prevent="col == 0 || focusButton(col-1, line)"
                  @keydown.enter="cell.header.editable && (locked = true, clickButton(cell, col, line))"
                  @keydown.exact.prevent="handleButtonKeydown({cell, col, line}, $event)"
                  type="button"
                  >
                  <span v-if="cell.header.type == 'date'">
                    {{  cell.text | date }}
                  </span>
                  <span v-else>
                    {{ cell.text }}
                  </span>
                </button>
                <input                 
                  v-if="editing == cell"
                  :ref="`form-data-table__input--${col}-${line}`"
                  :value="stuckInput || cell.value"
                  :style="{ width: cell.header.width + 'px' }"
                  :type="cell.header.type == 'float' || cell.header.type == 'integer' ? 'number' : cell.header.type == 'date' ? 'date' : 'text'"
                  @blur="inputBlur({ row, cell }, $event)"
                  @keydown.exact.enter="line < currentItems.length-1 ? focusButton(col, line+1) : ($event.target.blur(), focusButtonItself(col, line))"
                  @keydown.shift.enter="line > 0 ? focusButton(col, line-1) : ($event.target.blur(), focusButtonItself(col, line))"
                  @keydown.up="locked || line == 0 || ($event.preventDefault(), focusButton(col, line-1))"
                  @keydown.down="locked || line == currentItems.length-1 || ($event.preventDefault(), focusButton(col, line+1))"
                  @keydown.left="locked || col == 0 || ($event.preventDefault(), focusButton(col-1, line))"
                  @keydown.right="locked || col == computedHeaders.length-1 || ($event.preventDefault(), focusButton(col+1, line))"
                  @keydown.esc="hasCancelled = true, $event.target.blur(), focusButtonItself(col, line)"
                  class="form-data-table__input"
                  >
                  
              </td>
            </tr>
          </template>
          
          <!-- No Results  -->
          <template #no-results>
            <v-alert :value="true" color="error" icon="mdi-alert-outline" class="mt-3">
              <span v-if="search.value.length > 0">A pesquisa por "{{ search.value }}" não retornou registros, confira os filtros selecionados.</span>
              <span v-else>Os filtros selecionados não retornaram registros.</span>
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
const { mapState:registersState, mapActions:registersActions } = createNamespacedHelpers('registers')
import XLSX from "xlsx"
import FormDataTableFilters from "@/components/Registers/FormDataTableFilters"
import FormDataTableToolbar from "@/components/Registers/FormDataTableToolbar"
import FormDataTableErrors from "@/components/Registers/FormDataTableErrors"
import FormDataTableBatch from "@/components/Registers/FormDataTableBatch"
import { Table } from "@/plugins/cell"
export default {
  name: "FormDataTable",
  components: { FormDataTableFilters, FormDataTableToolbar, FormDataTableErrors, FormDataTableBatch },
  props: {
    headers: Array,
    formMaster: Object,
    items: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Array,
      default: () => []
    },
    color: {
      type: String,
      default: "teal"
    },
    dark: {
      type: Boolean,
      default: false
    },
    exportXls: {
      type: Boolean,
      default: true
    },
    xlsxName: {
      type: String,
      default: 'itens'
    },
  },

  data() {
    return {
      hasCancelled: false,
      currentItems: [],
      search: {
        value: "",
      },
      openFilter: false,
      filtersModels: {
        invalidCells: false,
      },
      itemsPerPage: 10,
      editing: {},
      stuckInput: "",
      locked: false,
      table: null,
      invalidCells: []
    }
  },

  computed: {
    computedHeaders() {
      const headers = this.headers.filter(h => !h.hide)

      return this.readonly
        ? headers.map(h => {
            return {
              ...h,
              editable: false,
            }
          })
        : headers.map(h => {
            return {
              ...h,
            }
          })
    },
    computedFilters() {
      const filters = this.headers.filter(x => x.filterable === true)
      filters.forEach(el => {
        const ixH = this.headers.findIndex(x => x.value === el.value)
        switch (el.filterType) {
          case 'switch':
            this.headers[ixH].filter = (header, search, row) => {
              const cell = row.getCellFromHeader(header)
              const validated = (cell.value || !this.filtersModels[el.value])
              if (this.filtersModels.invalidCells)
                return validated && !row.validated()
              return validated
            }
            break;
        
          default:
            this.headers[ixH].filterItems =  [ ...new Set(this.items.map(x => x[el.value])) ]
            this.headers[ixH].filter = (header, search, row) => {
              if (!this.filtersModels[el.value])
                return true
              if (this.filtersModels[el.value].length === 0)
                return true

              const cell = row.getCellFromHeader(header)
              const validated = this.filtersModels[el.value].findIndex(n => n == cell.value) > -1
              if (this.filtersModels.invalidCells)
                return validated && !row.validated()
              return validated
            }
            break;
        }
      })
      filters.push({
        filterType: 'switch',
        filterLabel: 'Com erro',
        value: 'invalidCells',
      })
      return filters
    },
    totalize() {
      let totalize = null

      if (this.items && this.items.length > 0) {
        totalize = []
        this.headers.filter(x => x.totalize).forEach(elx => {
          const total = this.items.map(x => x[elx.value]).reduce((acc, cur) => acc + cur)
          const prefix = elx.prefix ? elx.prefix + ' ' : ''
          const suffix = elx.suffix ? ' ' + elx.suffix : ''
          totalize.push({
            label: elx.text, 
            text: prefix + total.toLocaleString('pt-br', { minimumFractionDigits: elx.precisionText, maximumFractionDigits: elx.precisionText}) + suffix,
            value: elx.value
          })
        });
      }
      return totalize
    },
    
    invalidIdentifiers() {
      return this.invalidCells.map(cell => {
        return {
          cell,
          identifierCells: this.identifierHeaders.reduce((arr, header)=> {
            const found = cell.row.getCellFromHeader(header.value)
            if (found)
              return [...arr, found]
            else
              return arr
          }, [])
        }
      })
    },
    validated() {
      return !(this.invalidCells.length > 0)
    },
    isFiltered() {
      if (this.search.value.length > 0)
        return true
      
      return Object.entries(this.filtersModels)
        .some(([_, model]) => {
          if (Array.isArray(model) && model.length > 0)
            return true
          if (typeof model == "boolean" && model)
            return true
          return false
        })
    },
  },

  watch: {
    items(items) {
      if (!items)
        return

      const headers = this.readonly
        ? this.headers.map(h => ({ ...h, editable: false }))
        : this.headers
      
      const table = new Table({ headers, items, readonly: this.readonly })
      this.table = table
    },

    invalidCells() {
      this.$emit('validated', this.validated)
    },
  },

  created() {
    this.fetchFiltersCollections()
  },

  methods: {
    ...registersActions(['get', 'getById']),
    submit() {
      this.$emit('submit', this.items)
    },

    validate() {
      return this.$refs.form.validate()
    },

    getModel() {
      return this.items
    },

    getTable() {
      return this.table
    },

    getTotalize() {
      return this.totalize
    },

    customSort(rows, sortBy, sortDesc) {
      if (sortBy.length == 0)
        return rows

      // Leva em consideração apenas um item, não permitindo ordenar por mais de uma coluna.
      const { by, desc } = { by: sortBy[0], desc: sortDesc[0] }
      
      const orderned = rows.sort((rowA, rowB) => {
        const cellA = rowA.getCellFromHeader(by)
        const cellB = rowB.getCellFromHeader(by)
        if (cellA.value < cellB.value) {
          return desc ? 1 : -1;
        } else if (cellA.value > cellB.value) {
          return desc ? -1 : 1;
        }
        return 0;
      })
      return orderned
    },

    customFilter(headerValue, search, row) {
      const caseSensitive = false
      const cell = row.getCellFromHeader(headerValue)

      let searchFilter = true
      if (search) {
        searchFilter = caseSensitive
          ? cell.text.includes(search)
          : cell.text.toLowerCase().includes(search.toLowerCase())
      }

      return searchFilter
    },

    calculateHeaderWidth(cell, line) {
      cell.header.width = this.getColumnWidth(line) + (cell.header.type == 'date' ? 70 : 0)
    },

    clickButton(cell, col, line) {
      this.calculateHeaderWidth(cell, line)
      this.editing = cell
      this.$nextTick(() => {
        this.getInput(col, line).focus()
      })
    },

    handleButtonKeydown({ cell, col, line }, { keyCode, key }) {
      if (!cell.header.editable)
        return
      
      const isNumber = (48 <= keyCode && keyCode <= 57) || (96 <= keyCode && keyCode <= 105)
      const isChar = 65 <= keyCode && keyCode <= 90
      const isCcdil = keyCode == 186
      if ( isNumber || isChar || isCcdil ) {
        if (this.editing == cell) {
          this.stuckInput = this.stuckInput + key
        } else {
          this.stuckInput = key
          this.clickButton(cell, col, line)
        }
      }
    },

    inputBlur({ cell, row }, event) {
      this.editing = null
      this.locked = false
      this.stuckInput = ''

      if (this.hasCancelled)
        this.hasCancelled = false
      else
        row.process({ value: event.target.value, cell, formMaster: this.formMaster.getModel()  })
    },

    getButton(col, line) {
      return this.$refs[`form-data-table__button--${col}-${line}`][0]
    },

    getInput(col, line) {
      return this.$refs[`form-data-table__input--${col}-${line}`][0]
    },

    getTd(line) {
      return this.$refs[`form-data-table-td--${line}`][0]
    },

    focusButton(x, y) {
      const button = this.getButton(x, y)
      button.focus()
    },

    focusButtonItself(col, line) {
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.focusButton(col, line)
        })
      })
    },

    getColumnWidth(line) {
      const td = this.getTd(line)
      return td.clientWidth
    },

    async fetchFiltersCollections() {
      await Promise.all(this.filters.map(async filter => {
        if (filter.method && filter.method.collectionName) {
          const response = await this.get({collection: filter.method.collectionName, register: filter.method.collectionName, orderBy: 'indice'})
          filter.method.values = response.map(item => item[filter.method.collectionField])
          return response
        }
        return Promise.resolve()
      }))
    },

    exportXlsx() {
      const headers = this.headers.filter(h => h.export)
      const header = headers.map(h => h.text);
      const items = this.items.map(item => headers.reduce((acc, header) => ({...acc, [header.text]: item[header.value]}) , {}));
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(items, { header });

      // adiciona as fórmulas em formato excel para as células das colunas que possuem essa configuração
      items.slice(1).forEach((_, idx) => {
        headers.forEach((header, i) => {
          if (!header.xlsxFormula)
            return
          
          const cellKey = `${String.fromCharCode(65 + i)}${idx + 2}`
          try {
            ws[cellKey].f = header.xlsxFormula.trim().replaceAll('x', `${idx + 2}`)
          } catch (err) {
            console.error(err)
          }
        })
      })

      XLSX.utils.book_append_sheet(wb, ws, `Sheet`)

      const exportFileName = `${this.xlsxName}.xlsx`;
      XLSX.writeFile(wb, exportFileName)
    },

    importXlsx(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          /* Parse data */
          const bstr = e.target.result;
          const wb = XLSX.read(bstr, {type:'binary'});

          /* Seleciona a primeira página */
          const wsname = wb.SheetNames[0];
          const ws = wb.Sheets[wsname];

          /*
          * Transforma os dados da página em um array com as linhas da página, onde cada linha também é um array com os itens ordenados pela coluna.
          * data[0] = primeira linha, que é a linha dos headers
          * data[1...n] = todos os dados da tabela
          */
          const data = XLSX.utils.sheet_to_json(ws, {header:1});
          const headers = data[0]
          const items = data.slice(1)
          
          /*
          * Filtra os headers que possuem valor correspondente com alguma coluna na tabela atual
          * importables = array com os headers editáveis
          * identifiers = array com os headers que são identificadores (é assumido que os valores sao únicos)
          */
          const { importables, identifiers } = headers
            .reduce(({ importables, identifiers }, value, idx) => {
              const header = this.headers.find(h => h.text == value)
              const newEditables = header && header.import ? [...importables, {headerText: header.text, headerValue: header.value, value, idx}]: importables
              const newIdentifiers = header && header.identifier ? [...identifiers, {headerText: header.text,headerValue: header.value, value, idx}] : identifiers
              return {
                importables: newEditables,
                identifiers: newIdentifiers
              }
            }, { importables: [], identifiers: [] })


          items.forEach(row => {
            /*
            * percorre cada coluna identificadora (`identifiers`) para fazer as validações;
            * busca o item que corresponde com o valor presente em cada coluna identificadora da linha atual da planilha (`row`)
            * verifica se todos os resultados são do mesmo item (para caso de haver mais de uma coluna identificadora e os valores correspondentes no item serem de itens diferentes)
            */ 
            let i = 0
            let lastItemIdx = null
            let currentItemIdx = null
            do {
              lastItemIdx = currentItemIdx
              currentItemIdx = this.items.findIndex(item => item[identifiers[i].headerValue] == row[identifiers[i].idx])
              
              // caso não encontre um item correspondente, ou corresponda a mais de um item não atualiza o item
              if (currentItemIdx == -1 || (lastItemIdx != null && lastItemIdx != currentItemIdx))
                return
              
              i++
            } while (i < identifiers.length)


            const idx = currentItemIdx
            const item = this.items[idx]
            const tableRow = this.table.findRow(item)
            for (const importable of importables) {
              const header = this.headers.find(header => header.value == importable.headerValue)
              const value = row[importable.idx]
              const cell = tableRow.cells[this.table.headersHashMap[header.value]]
              tableRow.process({ cell, value })
            }
          })

          // @TODO considerar a viabilidade de usar raeder.onloadend
          this.$toast.success('Arquivo importado!', { position: 'bottom' })
        } catch (err) {
          this.$toast.error('Falha ao importar', { position: 'bottom' })
          console.error(err)
        }
      }

      reader.onerror = (err) => {
        this.$toast.error('Falha ao importar', { position: 'bottom' })
        console.error(err)
      }
      
      const fileExtension = file.name.split('.').pop().toLowerCase()
      if (!['xlsx', 'xls'].includes(fileExtension)) {
        this.$toast.error('Falha ao importar: Formato não suportado.', { position: 'bottom' })
        return
      }

      reader.readAsBinaryString(file);
    },

    scrollTop() {
      const el = this.$refs.dataTable.$el
      if (el) {
        this.$nextTick(() => {
          el.scrollIntoView(false)
        })
      }
    },

    scrollToLine(line, options) {
      const el = this.$refs[`form-data-table-tr--${line}`]
      if (el) {
        el.scrollIntoView(options)
      }
    },

    scrollToCell(cell) {
      this.clearAndInvalidCellsFilter()
      this.$nextTick(() => {
        const pos = this.currentCellPosition(cell)
        if (pos) {
          this.scrollToLine(pos.line)
          this.focusButton(pos.col, pos.line)
        } else {
          console.warn(`scrollToCell: Couldn't get current position of cell in column "${cell.header.value}"`)
        }
      })
    },

    currentCellPosition(cell) {
      const line = this.currentItems.findIndex(({ line }) => line == cell.y)
      const col = this.headers.findIndex(h => h.value == cell.header.value)
      if (col >= 0 && line >= 0)
        return { col, line }
      else 
        return null
    },

    clearAndInvalidCellsFilter() {
      this.$refs['form-data-table-filters'].clearFilters({invalidCells: true})
    },

    applyBatch({ item, inputs }) {
      const filteredItems = this.$refs.dataTable.$children[0].filteredItems
      filteredItems.forEach(row => {
        for (const input of inputs) {
          const value = item[input.value]
          const header = input.value
          if (!value)
            continue

          const cell = row.getCellFromHeader(header)
          row.process({ value, cell })
        }
      })
    },

    print(...args) {
      // console.info(...args)
    }
  },
}
</script>

<style lang="scss">
  .v-badge.small .v-badge__badge {
    width: 16px;
    height: 16px;
    min-width: auto;
  }

  .form-data-table__validations {
    background-color: rgba(247, 149, 149, 0.534);
  }

  .form-data-table__row {
    &.form-data-table__row--invalid {
      border: 1px solid rgba(255, 0, 0, 0.33);
      background-color: rgba(247, 149, 149, 0.534);
    }

  }

  .form-data-table__button {
    display: block;
    height: 100%;
    padding: 0 12px;
    white-space: nowrap;

    &:hover {
      background-color: rgba(0, 0, 0, .12);
    }

    &.form-data-table__button--invalid {
      border: 1px solid rgba(255, 0, 0, 0.33);
      border-radius: 3px;
      background-color: #fd484860;
    }

    &:focus {
      background-color: #eeeeee;
      border: 1px solid rgba(0, 0, 0, .33);
      border-radius: 3px;
      padding: 0 11px;
    }
  }

  .form-data-table__input {
    display: block;
    height: 100%;
    padding: 0 12px;

    outline: 0;
    border: 1px solid rgba(0, 0, 0, .33);
    border-radius: 3px;
  }
</style>